import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import "./EnquiryOverviewPanel.scss";
import KanbanBoard from "./Kanbanboard";
import LoadingScreen from "../LoadingScreen/LoadingScreen";

// ─── Constants ────────────────────────────────────────────────────────────────

// Page size for the per-enquiry paginated detail fetch
// (/pagination/sales-enquiries) used to fill in an expanded card's stage
// history + purchase order timeline a page at a time as the user scrolls,
// instead of relying on whatever was already loaded in bulk.
const ENQUIRY_DETAIL_PAGE_SIZE = 10;

// `/stage-summary` deliberately returns only the stage totals and a compact
// history entry (id, code and amount). The date filters need `createdAt`,
// which is available on the paginated stage-detail endpoint instead.
const OVERVIEW_STAGE_PAGE_SIZE = 100;

const ENQUIRY_STAGES = [
  "Enquiry",
  "Design",
  "Proposal & Negotiation",
  "Order Won",
  "Order Lost",
];

const QUICK_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "new", label: "New" },
  { key: "design", label: "In Design" },
  { key: "negotiation", label: "Negotiation" },
  { key: "won", label: "Order Won" },
  { key: "ops", label: "Ops Running" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "lost", label: "Lost" },
];

const LIST_TABS = [
  { key: "enquiries", label: "Enquiries" },
  { key: "operations", label: "Operations" },
];

// `/stage-summary` may include the same enquiry under more than one historic
// stage. The overview needs one row per enquiry, so retain its furthest stage
// and expose that stage explicitly for the list badges and progress track.
const stageSummaryToEnquiries = (summary) => {
  const stages = summary || [];

  // Keep this compatible with callers that already supply full enquiry
  // objects (for example, the paginated detail endpoint). `/stage-summary`
  // itself returns stage wrappers with a nested, compact `histories` array,
  // which is handled by the grouped branch below.
  const looksLikeFlatEnquiryList = stages.every(
    (item) =>
      item &&
      item.enquiryId !== undefined &&
      Array.isArray(item.stageHistories),
  );
  if (looksLikeFlatEnquiryList) {
    return stages.map((eq) => ({
      ...eq,
      totalQuotationAmount: eq.totalQuotationAmount || 0,
      totalEstimateAmount: eq.totalEstimateAmount ?? 0,
      discountPercentage: eq.discountPercentage ?? 0,
      notes: eq.notes ?? null,
      lead: eq.lead ?? null,
      client: eq.client ?? null,
      purchaseOrders: eq.purchaseOrders ?? [],
      stageHistories: eq.stageHistories ?? [],
      enquiryStages: eq.enquiryStages ?? [],
    }));
  }

  const enquiryStages = stages
    .map((stage, index) => ({
      enquiryStageId: stage.enquiryStageId,
      stageName: stage.stageName,
      sequenceNumber:
        ENQUIRY_STAGES.indexOf(stage.stageName) >= 0
          ? ENQUIRY_STAGES.indexOf(stage.stageName) + 1
          : index + 1,
    }))
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber);

  const enquiryById = new Map();
  stages.forEach((stage, summaryIndex) => {
    const stageRank = ENQUIRY_STAGES.indexOf(stage.stageName);
    const rank = stageRank >= 0 ? stageRank : summaryIndex;

    (stage.histories || []).forEach((history) => {
      const existing = enquiryById.get(history.enquiryId);
      if (existing && existing.rank > rank) return;

      enquiryById.set(history.enquiryId, {
        rank,
        enquiry: {
          enquiryId: history.enquiryId,
          enquiryCode: history.enquiryCode,
          totalQuotationAmount: history.totalQuotationAmount || 0,
          currentStageName: stage.stageName,
          enquiryStages,
          // The filter logic (date range, pending/cancelled/won/lost quick
          // filters, employee filter, company search) reads these fields
          // off the enquiry object. `/stage-summary` histories were only
          // being read for 3 fields before, silently dropping everything
          // else the API sends back — copy the rest through defensively
          // (undefined if the API doesn't actually include a field, same
          // as before, but no longer discarded if it does).
          status: history.status ?? null,
          type: history.type ?? null,
          priority: history.priority ?? null,
          createdAt: history.createdAt ?? null,
          expectedCloseDate: history.expectedCloseDate ?? null,
          closedDate: history.closedDate ?? null,
          totalEstimateAmount: history.totalEstimateAmount ?? 0,
          discountPercentage: history.discountPercentage ?? 0,
          notes: history.notes ?? null,
          lead: history.lead ?? null,
          client: history.client ?? null,
          purchaseOrders: history.purchaseOrders ?? [],
          stageHistories: [
            {
              enquiryStageId: stage.enquiryStageId,
              stageName: stage.stageName,
              stageType: stage.stageType ?? null,
              enquiryStatus: history.enquiryStatus ?? "PROCESSING",
              assignedTo: history.assignedTo ?? null,
              startedAt: history.startedAt ?? null,
            },
          ],
        },
      });
    });
  });

  return [...enquiryById.values()]
    .sort((a, b) => a.rank - b.rank)
    .map(({ enquiry }) => enquiry);
};

const getPagedStageEnquiries = async (
  summary,
  axiosInstance,
  getApiEndpoint,
) => {
  const stages = Array.isArray(summary) ? summary : [];
  const recordsByStage = await Promise.all(
    stages.map(async (stage) => {
      const total = Number(stage.totalEnquiries) || 0;
      const pageCount = Math.ceil(total / OVERVIEW_STAGE_PAGE_SIZE);
      if (!stage.enquiryStageId || pageCount === 0) return [];

      const pages = await Promise.all(
        Array.from({ length: pageCount }, (_, page) =>
          axiosInstance
            .get(getApiEndpoint("/pagination/sales-enquiries"), {
              params: {
                enquiryStageId: stage.enquiryStageId,
                page,
                size: OVERVIEW_STAGE_PAGE_SIZE,
              },
            })
            .then((response) => response.data?.data || []),
        ),
      );

      return pages.flat();
    }),
  );

  // A record should belong to one current stage, but dedupe defensively so a
  // backend overlap cannot make summary counts or date-filter results repeat.
  return [
    ...new Map(
      recordsByStage.flat().map((item) => [item.enquiryId, item]),
    ).values(),
  ];
};

const initials = (name) => {
  if (!name) return "?";
  return (
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?"
  );
};

const capitalize = (s) =>
  s
    ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, " ")
    : "—";

const fmtDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
};

const fmtMoney = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });

const toDateInputValue = (date) => {
  const d = new Date(date);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const getMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const getYearStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1);
};

const parseDateOnly = (date) => {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const getEnquiryDate = (eq) => {
  const stageHistories = eq.stageHistories || [];
  const purchaseOrders = eq.purchaseOrders || [];
  // The date filters (MTD/YTD/custom range) are meant to answer "when was
  // this enquiry created", so createdAt must be checked first. The other
  // fields are only fallbacks for payloads that don't carry createdAt —
  // previously createdAt was checked last, so it was effectively never
  // used whenever expectedCloseDate/closedDate were populated (which is
  // almost always), making the date filters match against close dates
  // instead of creation dates.
  return (
    eq.createdAt ||
    stageHistories[0]?.startedAt ||
    eq.expectedCloseDate ||
    eq.closedDate ||
    purchaseOrders[0]?.orderDate ||
    null
  );
};

const getOperationDate = (operation) =>
  operation.createdAt ||
  operation.startedAt ||
  operation.updatedAt ||
  operation.endedAt ||
  operation.purchaseOrders?.[0]?.orderDate ||
  null;

const normalizeStatus = (status) => (status || "PENDING").toUpperCase();

const isCompletedStatus = (status) =>
  ["COMPLETED", "COMPLETE", "DONE"].includes(normalizeStatus(status));

const isActiveStatus = (status) =>
  ["PROCESSING", "IN_PROGRESS", "IN PROGRESS", "ASSIGNED"].includes(
    normalizeStatus(status),
  );

const isCancelledStatus = (status) =>
  ["CANCELLED", "CANCELED", "CANCEL", "REJECTED", "LOST", "FAILED"].includes(
    normalizeStatus(status),
  );

const getTerminalStatusLabel = (eq) => {
  const statuses = [
    eq.status,
    ...(eq.stageHistories || []).map((stage) => stage.enquiryStatus),
  ].map(normalizeStatus);
  const stageNames = (eq.stageHistories || []).map((stage) =>
    (stage.stageName || "").toLowerCase(),
  );

  if (
    statuses.includes("CANCELLED") ||
    statuses.includes("CANCELED") ||
    statuses.includes("CANCEL")
  )
    return "Cancelled";
  if (statuses.includes("REJECTED") || statuses.includes("FAILED"))
    return "Rejected";
  if (
    statuses.includes("LOST") ||
    stageNames.some((stage) => stage.includes("lost"))
  )
    return "Lost";
  return "Cancelled";
};

const getOpsStageStatus = (statuses) => {
  if (statuses.some(isCancelledStatus)) return "CANCELLED";
  if (statuses.length > 0 && statuses.every(isCompletedStatus))
    return "COMPLETED";
  if (statuses.some(isActiveStatus)) return "PROCESSING";
  return "PENDING";
};

const getOrderOpsStages = (order) => {
  const map = new Map();

  (order?.products || []).forEach((product) => {
    (product.operationStages || []).forEach((stage) => {
      const key =
        stage.operationStageId ||
        stage.operationStageHistoryId ||
        stage.operationStageName;
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          ...stage,
          statuses: [],
        });
      }

      map.get(key).statuses.push(stage.status || "PENDING");
    });
  });

  return [...map.values()]
    .map((stage) => ({
      ...stage,
      status: getOpsStageStatus(stage.statuses),
    }))
    .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
};

const getAllOpsStages = (eq) =>
  (eq.purchaseOrders || []).flatMap((order) => getOrderOpsStages(order));

const hasTerminalLostStatus = (eq) => {
  const stageHistories = eq.stageHistories || [];

  return stageHistories.some(
    (s) =>
      s.stageType === "Lost" &&
      ["PROCESSING", "COMPLETED", "LOST"].includes(
        normalizeStatus(s.enquiryStatus),
      ),
  );
};

const getDisplayStageName = (eq) => {
  const state = resolveEnquiryState(eq);
  if (state.isLost) return state.terminalStatusLabel;
  if (state.isWon) return "Order Won";
  return state.currentStageName || "Enquiry";
};

const incrementMap = (map, key, amount = 1) => {
  const label = key || "Unknown";
  map.set(label, (map.get(label) || 0) + amount);
};

const getCountItems = (map) =>
  [...map.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

const isWithinRange = (date, start, end) => {
  const value = parseDateOnly(date);
  if (!value) return false;
  if (start && value < parseDateOnly(start)) return false;
  if (end && value > parseDateOnly(end)) return false;
  return true;
};

// Returns { enquiryStageIndex, currentStageName, status, isLost, isWon, opsStages, currentOpsStage }
const resolveEnquiryState = (eq) => {
  const stageHistories = eq.stageHistories || [];

  // Determine which enquiry stage we are at
  const stageNames = stageHistories.map((s) => s.stageName || "");
  const hasCancelledStatus =
    isCancelledStatus(eq.status) ||
    stageHistories.some((s) => isCancelledStatus(s.enquiryStatus));
  const isLost = hasTerminalLostStatus(eq);
  const isWon = stageNames.some((s) => s.toLowerCase().includes("won"));
  const terminalStatusLabel = hasCancelledStatus
    ? getTerminalStatusLabel(eq)
    : "Lost";

  // Current stage = last stage that is Processing, or last stage overall
  const currentStage =
    stageHistories.findLast?.((s) => isActiveStatus(s.enquiryStatus)) ||
    stageHistories[stageHistories.length - 1] ||
    null;

  const currentStageName =
    currentStage?.stageName || eq.currentStageName || "Enquiry";

  // Map to canonical ENQUIRY_STAGES index for progress bar
  const enquiryStageIndex = (() => {
    if (isLost) return 4;
    if (isWon) return 3;
    const lower = currentStageName.toLowerCase();
    if (lower.includes("proposal") || lower.includes("negotiation")) return 2;
    if (lower.includes("design")) return 1;
    return 0;
  })();

  // Operations stages
  const orders = eq.purchaseOrders || [];
  const activeOrder =
    orders.find((o) => isActiveStatus(o.orderStatus)) ||
    orders.find((o) => normalizeStatus(o.orderStatus) === "PENDING") ||
    orders.find((o) =>
      (o.products || []).some((p) => p.operationStages?.length),
    ) ||
    orders[orders.length - 1];

  const opsStages = getOrderOpsStages(activeOrder);
  const currentOpsStage =
    opsStages.find((s) => s.status === "PROCESSING") ||
    opsStages.find((s) => s.status === "PENDING") ||
    null;

  const needsAttention =
    !isLost &&
    !isWon &&
    eq.expectedCloseDate &&
    new Date(eq.expectedCloseDate) < new Date();

  return {
    enquiryStageIndex,
    currentStageName,
    isLost,
    isWon,
    terminalStatusLabel,
    opsStages,
    currentOpsStage,
    needsAttention,
  };
};

// ─── Enquiry progress dots ────────────────────────────────────────────────────

const EnquiryProgress = ({
  enquiryStageIndex,
  isLost,
  stageHistories,
  terminalStatusLabel,
}) => {
  const completedNames = new Set(
    (stageHistories || [])
      .filter((s) => isCompletedStatus(s.enquiryStatus))
      .map((s) => s.stageName),
  );
  const cancelledNames = new Set(
    (stageHistories || [])
      .filter((s) => isCancelledStatus(s.enquiryStatus))
      .map((s) => s.stageName),
  );

  return (
    <div className="eq-progress">
      {ENQUIRY_STAGES.slice(0, 4).map((stage, i) => {
        const isDone = completedNames.has(stage) || i < enquiryStageIndex;
        const isCancelled = cancelledNames.has(stage);
        const isActive = i === enquiryStageIndex && !isLost;

        let cls = "ep-step";
        if (isCancelled) cls += " lost";
        else if (isDone) cls += " done";
        else if (isActive) cls += " active";
        else cls += " pending";

        return (
          <React.Fragment key={stage}>
            <div className={cls} title={stage}>
              <div className="ep-dot">
                {isDone && !isCancelled && (
                  <svg width="7" height="7" viewBox="0 0 10 10">
                    <polyline
                      points="1.5,5 4,7.5 8.5,2.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {isCancelled && (
                  <svg width="7" height="7" viewBox="0 0 10 10">
                    <line
                      x1="2"
                      y1="2"
                      x2="8"
                      y2="8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8"
                      y1="2"
                      x2="2"
                      y2="8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
              <span className="ep-label">{stage}</span>
            </div>
            {i < 3 && (
              <div
                className={`ep-line ${isDone || (isActive && i < enquiryStageIndex) ? "done" : ""}`}
              />
            )}
          </React.Fragment>
        );
      })}
      {isLost && (
        <>
          <div className="ep-line" />
          <div className="ep-step lost" title={terminalStatusLabel}>
            <div className="ep-dot">
              <svg width="7" height="7" viewBox="0 0 10 10">
                <line
                  x1="2"
                  y1="2"
                  x2="8"
                  y2="8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="8"
                  y1="2"
                  x2="2"
                  y2="8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <span className="ep-label">{terminalStatusLabel}</span>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Operations progress pills ────────────────────────────────────────────────

const OpsProgress = ({ opsStages }) => {
  if (!opsStages || opsStages.length === 0) return null;

  return (
    <div className="ops-progress">
      <span className="ops-label">Ops</span>
      <div className="ops-stages">
        {opsStages.map((os, i) => {
          const s = normalizeStatus(os.status);
          let cls = "ops-pill";
          if (s === "COMPLETED") cls += " done";
          else if (s === "PROCESSING") cls += " active";
          else if (s === "CANCELLED") cls += " cancelled";
          else cls += " pending";

          return (
            <span
              key={os.operationStageId || i}
              className={cls}
              title={`${os.operationStageName}: ${capitalize(s)}`}
            >
              {s === "COMPLETED" && (
                <svg width="6" height="6" viewBox="0 0 10 10">
                  <polyline
                    points="1.5,5 4,7.5 8.5,2.5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {s === "CANCELLED" && (
                <svg width="6" height="6" viewBox="0 0 10 10">
                  <line
                    x1="2"
                    y1="2"
                    x2="8"
                    y2="8"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8"
                    y1="2"
                    x2="2"
                    y2="8"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {os.operationStageName}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ─── Stage Timeline ───────────────────────────────────────────────────────────

const StageTimeline = ({ stageHistories }) => {
  if (!stageHistories || stageHistories.length === 0) return null;

  return (
    <div className="stage-timeline">
      {stageHistories.map((stage, i) => {
        const s = normalizeStatus(stage.enquiryStatus);
        const isCompleted = isCompletedStatus(s);
        const isActive = isActiveStatus(s);
        const isCancelled = isCancelledStatus(s);
        const isLast = i === stageHistories.length - 1;

        let dotCls = "stl-dot";
        if (isCompleted) dotCls += " stl-dot--done";
        else if (isActive) dotCls += " stl-dot--active";
        else if (isCancelled) dotCls += " stl-dot--lost";
        else dotCls += " stl-dot--pending";

        return (
          <div
            className="stl-entry"
            key={stage.enquiryStageHistoryId || stage.enquiryStageId || i}
          >
            {/* Spine */}
            <div className="stl-spine">
              <div className={dotCls}>
                {isCompleted && (
                  <svg width="7" height="7" viewBox="0 0 10 10">
                    <polyline
                      points="1.5,5 4,7.5 8.5,2.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {isCancelled && (
                  <svg width="7" height="7" viewBox="0 0 10 10">
                    <line
                      x1="2"
                      y1="2"
                      x2="8"
                      y2="8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8"
                      y1="2"
                      x2="2"
                      y2="8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                {isActive && <div className="stl-pulse" />}
              </div>
              {!isLast && <div className="stl-line" />}
            </div>

            {/* Content */}
            <div className="stl-content">
              <div className="stl-head">
                <span className="stl-name">{stage.stageName || "—"}</span>
                <span
                  className={`stl-badge stl-badge--${isCompleted ? "done" : isActive ? "active" : isCancelled ? "lost" : "pending"}`}
                >
                  {capitalize(stage.enquiryStatus || "Pending")}
                </span>
              </div>
              <div className="stl-meta">
                {stage.stageType && (
                  <span className="stl-type">{stage.stageType}</span>
                )}
                {stage.assignedTo && <span>👤 {stage.assignedTo}</span>}
                {stage.startedAt && (
                  <span>Started {fmtDate(stage.startedAt)}</span>
                )}
                {stage.endedAt && <span>Ended {fmtDate(stage.endedAt)}</span>}
                {stage.dueAt && <span>Due {fmtDate(stage.dueAt)}</span>}
              </div>
              {stage.remarks && (
                <div className="stl-remark">{stage.remarks}</div>
              )}
              {(stage.productEnquiryHistories || []).length > 0 && (
                <div className="stl-products">
                  {stage.productEnquiryHistories.slice(0, 6).map((p) => (
                    <span className="stl-product-chip" key={p.id}>
                      {p.productName}
                      {p.quantity ? ` ×${p.quantity}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const OpsTimeline = ({ purchaseOrders }) => {
  if (!purchaseOrders || purchaseOrders.length === 0) return null;

  return (
    <div className="ops-timeline">
      {purchaseOrders.map((order) => {
        const stages = getOrderOpsStages(order);
        return (
          <div className="opst-order" key={order.purchaseOrderId}>
            <div className="opst-order-head">
              <span className="opst-order-num">
                {order.orderNumber || `PO-${order.purchaseOrderId}`}
              </span>
              <span className="opst-order-status">
                {capitalize(order.orderStatus)}
              </span>
              {order.totalAmount > 0 && (
                <span className="opst-order-amt">
                  {fmtMoney(order.totalAmount)}
                </span>
              )}
            </div>
            <div className="stage-timeline">
              {stages.map((os, i) => {
                const s = normalizeStatus(os.status);
                const isCompleted = isCompletedStatus(s);
                const isActive = isActiveStatus(s);
                const isCancelled = isCancelledStatus(s);
                const isLast = i === stages.length - 1;

                let dotCls = "stl-dot";
                if (isCompleted) dotCls += " stl-dot--done";
                else if (isActive) dotCls += " stl-dot--active";
                else if (isCancelled) dotCls += " stl-dot--lost";
                else dotCls += " stl-dot--pending";

                return (
                  <div
                    className="stl-entry"
                    key={os.operationStageHistoryId || os.operationStageId || i}
                  >
                    <div className="stl-spine">
                      <div className={dotCls}>
                        {isCompleted && (
                          <svg width="7" height="7" viewBox="0 0 10 10">
                            <polyline
                              points="1.5,5 4,7.5 8.5,2.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                        {isCancelled && (
                          <svg width="7" height="7" viewBox="0 0 10 10">
                            <line
                              x1="2"
                              y1="2"
                              x2="8"
                              y2="8"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <line
                              x1="8"
                              y1="2"
                              x2="2"
                              y2="8"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                        {isActive && <div className="stl-pulse" />}
                      </div>
                      {!isLast && <div className="stl-line" />}
                    </div>
                    <div className="stl-content">
                      <div className="stl-head">
                        <span className="stl-name">
                          {os.operationStageName || "—"}
                        </span>
                        <span
                          className={`stl-badge stl-badge--${isCompleted ? "done" : isActive ? "active" : isCancelled ? "lost" : "pending"}`}
                        >
                          {capitalize(s)}
                        </span>
                      </div>
                      <div className="stl-meta">
                        {os.assignedEmployeeName && (
                          <span>👤 {os.assignedEmployeeName}</span>
                        )}
                        {os.operationStartedAt && (
                          <span>Started {fmtDate(os.operationStartedAt)}</span>
                        )}
                        {os.operationEndedAt && (
                          <span>Ended {fmtDate(os.operationEndedAt)}</span>
                        )}
                        {os.qcStatus && (
                          <span>QC: {capitalize(os.qcStatus)}</span>
                        )}
                      </div>
                      {os.remarks && (
                        <div className="stl-remark">{os.remarks}</div>
                      )}
                      {os.returned && (
                        <div className="stl-remark stl-remark--warn">
                          ↩ Returned
                          {os.returnedMessage ? `: ${os.returnedMessage}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CountList = ({ title, items, emptyLabel = "No data" }) => {
  const max = Math.max(...items.map((item) => item.count), 1);

  return (
    <div className="summary-block">
      <div className="summary-title">{title}</div>
      <div className="summary-list">
        {items.length === 0 ? (
          <div className="summary-empty">{emptyLabel}</div>
        ) : (
          items.map((item) => (
            <div className="summary-row" key={item.label}>
              <div className="summary-row-top">
                <span className="summary-name">{item.label}</span>
                <span className="summary-count">{item.count}</span>
              </div>
              <div className="summary-track">
                <div
                  className="summary-fill"
                  style={{ width: `${Math.max((item.count / max) * 100, 6)}%` }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const SummaryRail = ({ data }) => {
  const summaries = useMemo(() => {
    const statusMap = new Map();
    const stageStatusMap = new Map();
    const stageMap = new Map();
    const operationMap = new Map();

    data.forEach((eq) => {
      incrementMap(statusMap, capitalize(eq.status || "Pending"));
      (eq.stageHistories || []).forEach((stage) => {
        incrementMap(
          stageStatusMap,
          capitalize(stage.enquiryStatus || "Pending"),
        );
      });
      incrementMap(stageMap, getDisplayStageName(eq));
      getAllOpsStages(eq).forEach((stage) => {
        incrementMap(operationMap, stage.operationStageName || "Operation");
      });
    });

    return {
      statusItems: getCountItems(statusMap),
      stageStatusItems: getCountItems(stageStatusMap),
      stageItems: getCountItems(stageMap),
      operationItems: getCountItems(operationMap),
    };
  }, [data]);

  return (
    <aside className="summary-rail" aria-label="Enquiry summaries">
      <CountList title="Statuses" items={summaries.statusItems} />
      <CountList title="Stage statuses" items={summaries.stageStatusItems} />
      <CountList title="Enquiry stages" items={summaries.stageItems} />
      <CountList
        title="Operation stages"
        items={summaries.operationItems}
        emptyLabel="No operations"
      />
    </aside>
  );
};

const Field = ({ label, value }) => (
  <div className="detail-field">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value ?? "—"}</span>
  </div>
);

const NotesTooltip = ({ notes }) => {
  const [visible, setVisible] = React.useState(false);
  if (!notes) return null;
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span
        title="Notes"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fef9c3",
          border: "1px solid #fde047",
          cursor: "default",
          flexShrink: 0,
        }}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ca8a04"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
      </span>
      {visible && (
        <span
          style={{
            position: "absolute", // ← fixed typo
            top: "calc(100% + 6px)", // ← only top, renders below icon
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1e1b0e",
            color: "#fef3c7",
            fontSize: "0.75rem",
            lineHeight: 1.5,
            padding: "7px 10px",
            borderRadius: 8,
            whiteSpace: "pre-wrap",
            maxWidth: 260,
            width: "max-content",
            zIndex: 9999,
            boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
            pointerEvents: "none",
          }}
        >
          {notes}
          {/* Arrow points UP toward the icon */}
          <span
            style={{
              position: "absolute",
              bottom: "100%", // ← sits above the tooltip box
              left: "50%",
              transform: "translateX(-50%)",
              borderWidth: "0 5px 5px", // ← flipped
              borderStyle: "solid",
              borderColor: "transparent transparent #1e1b0e", // ← flipped
              display: "block",
              width: 0,
              height: 0,
            }}
          />
        </span>
      )}
    </span>
  );
};

// Merges a fresh page of records into an existing list, deduping by id so
// pages that overlap (or a re-fetched first page) don't produce duplicate
// entries in the rendered timeline.
const mergeById = (existing, incoming, idKey) => {
  if (!incoming || incoming.length === 0) return existing;
  const seen = new Set(existing.map((item) => item[idKey]));
  const merged = [...existing];
  incoming.forEach((item) => {
    const id = item[idKey];
    if (id == null || seen.has(id)) return;
    seen.add(id);
    merged.push(item);
  });
  return merged;
};

const EnquiryExpandedDetails = ({
  eq,
  onScroll,
  loadingMore = false,
  hasMore = false,
}) => {
  const purchaseOrders = eq.purchaseOrders || [];

  return (
    <div className="eq-expanded" onScroll={onScroll}>
      <div className="detail-grid">
        <Field label="Enquiry ID" value={eq.enquiryId} />
        <Field label="Enquiry code" value={eq.enquiryCode} />
        <Field label="Status" value={eq.status} />
        <Field label="Type" value={eq.type} />
        <Field label="Priority" value={eq.priority} />
        <Field label="Current stage" value={eq.currentStageName} />
        <Field label="Expected close" value={fmtDate(eq.expectedCloseDate)} />
        <Field label="Closed date" value={fmtDate(eq.closedDate)} />
        <Field label="Estimate" value={fmtMoney(eq.totalEstimateAmount)} />
        <Field label="Quotation" value={fmtMoney(eq.totalQuotationAmount)} />
        <Field label="Discount" value={`${eq.discountPercentage || 0}%`} />
        {eq.notes ? (
          <div
            className="detail-field detail-field--notes"
            style={{ gridColumn: "1 / -1" }}
          >
            <span className="detail-label">Notes</span>
            <span
              className="detail-value"
              style={{
                background: "#fefce8",
                border: "1px solid #fde047",
                borderRadius: 6,
                padding: "6px 10px",
                color: "#713f12",
                fontSize: "0.85rem",
                lineHeight: 1.6,
                display: "block",
                whiteSpace: "pre-wrap",
              }}
            >
              {eq.notes}
            </span>
          </div>
        ) : (
          <Field label="Notes" value={null} />
        )}{" "}
      </div>

      <div className="detail-columns">
        <div className="detail-section">
          <div className="detail-section-title">Lead</div>
          <Field label="Company" value={eq.lead?.companyName} />
          <Field label="Contact" value={eq.lead?.contactPerson} />
          <Field label="Email" value={eq.lead?.email} />
          <Field label="Phone" value={eq.lead?.phone} />
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Client</div>
          <Field label="Company" value={eq.client?.companyName} />
          <Field label="Contact" value={eq.client?.contactPerson} />
          <Field label="Email" value={eq.client?.email} />
          <Field label="Phone" value={eq.client?.phone} />
        </div>
      </div>

      <div className="detail-section">
        <div className="detail-section-title">Stage history</div>
        <StageTimeline stageHistories={eq.stageHistories} />
      </div>

      {purchaseOrders.length > 0 && (
        <div className="detail-section">
          <div className="detail-section-title">
            Purchase orders &amp; operations
          </div>
          <OpsTimeline purchaseOrders={purchaseOrders} />
        </div>
      )}

      {loadingMore && <div className="eq-expanded-loading">Loading more…</div>}
      {!hasMore && !loadingMore && (
        <div className="eq-expanded-end">End of history</div>
      )}
    </div>
  );
};

// ─── Single enquiry row ───────────────────────────────────────────────────────

const EnquiryRow = ({
  eq,
  expanded,
  onToggle,
  detail,
  pagination,
  loadingMore = false,
  onLoadMore,
}) => {
  const isLead = !!eq.lead;
  const entityName = isLead
    ? eq.lead?.companyName || "Unknown"
    : eq.client?.companyName || "Unknown";
  const code = eq.enquiryCode || `EQ-${eq.enquiryId}`;

  const {
    enquiryStageIndex,
    currentStageName,
    isLost,
    isWon,
    terminalStatusLabel,
    opsStages,
    currentOpsStage,
    needsAttention,
  } = useMemo(() => resolveEnquiryState(eq), [eq]);

  const priority = (eq.priority || "medium").toLowerCase();
  const type = (eq.type || "warm").toLowerCase();
  const expectedClose = fmtDate(eq.expectedCloseDate);

  const avatarInitials = initials(entityName);

  const enquiryId = eq.enquiryId;

  // Paginated stage-history / purchase-order detail for this card, from
  // /pagination/sales-enquiries?enquiryId=...&page=...&size=... . The
  // parent (EnquiryOverviewPanel) prefetches page 0 for every enquiry as
  // soon as the Overview tab's data loads, so `detail` is usually already
  // populated by the time a card is expanded — this just falls back to
  // whatever the bulk fetch provided while that prefetch is in flight.
  const mergedEq = useMemo(
    () => ({
      ...eq,
      stageHistories: detail?.stageHistories ?? eq.stageHistories ?? [],
      purchaseOrders: detail?.purchaseOrders ?? eq.purchaseOrders ?? [],
    }),
    [eq, detail],
  );

  // Loads the next page once the card's own scroll container nears the
  // bottom (infinite scroll per-card, rather than per-column/per-page).
  const handleDetailScroll = useCallback(
    (e) => {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
        onLoadMore?.(enquiryId);
      }
    },
    [onLoadMore, enquiryId],
  );

  return (
    <div className={`eq-item ${expanded ? "expanded" : ""}`} role="listitem">
      <div
        className={`eq-row ${isLost ? "row-lost" : ""} ${needsAttention ? "row-attention" : ""}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        {/* Left: avatar + name block */}
        <div className="eqr-identity">
          <div className={`eqr-avatar ${isLead ? "av-lead" : "av-client"}`}>
            {avatarInitials}
          </div>
          <div className="eqr-name-block">
            <div className="eqr-name">
              {entityName}
              {needsAttention && (
                <span
                  className="attention-dot"
                  title="Past expected close date"
                />
              )}
            </div>
            <div className="eqr-sub">
              <span className="eqr-code">{code}</span>
              {expectedClose && (
                <span className="eqr-date">{expectedClose}</span>
              )}
            </div>
          </div>
        </div>

        {/* Center: progress tracks */}
        <div className="eqr-tracks">
          <EnquiryProgress
            enquiryStageIndex={enquiryStageIndex}
            isLost={isLost}
            stageHistories={eq.stageHistories}
            terminalStatusLabel={terminalStatusLabel}
          />
          {opsStages.length > 0 && <OpsProgress opsStages={opsStages} />}
        </div>

        {/* Right: badges */}
        <div className="eqr-badges">
          <span className={`badge-type type-${type}`}>
            {capitalize(eq.type)}
          </span>
          <span className={`badge-priority prio-${priority}`}>
            {capitalize(eq.priority)}
          </span>
          {isWon && !isLost && currentOpsStage && (
            <span
              className="badge-ops-stage"
              title={`Current ops stage: ${currentOpsStage.operationStageName}`}
            >
              {currentOpsStage.operationStageName}
            </span>
          )}
          {!isWon && !isLost && (
            <span className="badge-stage">{currentStageName}</span>
          )}
          {isLost && <span className="badge-lost">{terminalStatusLabel}</span>}
          {isWon && !currentOpsStage && <span className="badge-won">Won</span>}
          <NotesTooltip notes={eq.notes} />
          <span className="badge-expand">
            {expanded ? "Hide" : "Details"}
          </span>{" "}
        </div>
      </div>
      {expanded && (
        <EnquiryExpandedDetails
          eq={mergedEq}
          onScroll={handleDetailScroll}
          loadingMore={loadingMore}
          hasMore={pagination?.hasMore ?? true}
        />
      )}
    </div>
  );
};

// ─── Unified KB Status Bar ────────────────────────────────────────────────────
//
// Reads stage columns from the same source as the Kanban:
//   enquiries tab  → eq.enquiryStages[]   (stageName as id)
//   operations tab → eq.operationStages[] (name as id)

// Mirror of Kanbanboard.jsx deriveEnquiryStageName
const _deriveEnqStageName = (eq) => {
  const sh = eq.stageHistories || [];
  if (sh.length === 0) return null;
  const active = sh.find((s) => isActiveStatus(s.enquiryStatus));
  if (active) return active.stageName || null;
  return sh[sh.length - 1]?.stageName || null;
};

// Mirror of Kanbanboard.jsx deriveOperationStageName
const _deriveOpsStageName = (eq) => {
  const allOps = (eq.purchaseOrders || []).flatMap((o) =>
    (o.products || []).flatMap((p) => p.operationStages || []),
  );
  if (allOps.length === 0) return null;
  const reached = [...allOps]
    .reverse()
    .find((s) => isActiveStatus(s.status) || isCompletedStatus(s.status));
  if (reached) return reached.operationStageName || null;
  return allOps[0]?.operationStageName || null;
};

const KbStatusBar = ({
  allData,
  filteredData,
  activeTab,
  operationSummary,
}) => {
  const stages = useMemo(() => {
    if (activeTab === "enquiries") {
      const map = new Map();
      // Build stages from allData so empty stages don't disappear
      allData.forEach((eq) => {
        (eq.enquiryStages || []).forEach((s) => {
          if (s.enquiryStageId && !map.has(s.enquiryStageId))
            map.set(s.enquiryStageId, s);
        });
      });
      return [...map.values()]
        .sort((a, b) => (a.sequenceNumber ?? 999) - (b.sequenceNumber ?? 999))
        .map((s) => ({ id: s.stageName, label: s.stageName }));
    }

    // Operations use the summary only to keep every configured stage visible;
    // their counts are calculated below from the filtered operation records.
    return (operationSummary || []).map((s) => ({
      id: String(s.operationStageId),
      label: s.operationStageName,
      totalOperations: s.totalCount || 0,
    }));
  }, [allData, activeTab, operationSummary]);

  const counts = useMemo(() => {
    const map = {};
    if (activeTab === "enquiries") {
      stages.forEach((s) => {
        map[s.id] = 0;
      });
      // Calculate counts using ONLY the filteredData
      filteredData.forEach((eq) => {
        const id = _deriveEnqStageName(eq);
        if (id && map[id] !== undefined) map[id]++;
      });
    } else {
      stages.forEach((s) => {
        map[s.id] = 0;
      });
      filteredData.forEach((operation) => {
        const id = String(operation.operationStageId);
        if (map[id] !== undefined) map[id]++;
      });
    }
    return map;
  }, [filteredData, activeTab, stages]);

  // Both data sources are already filtered by the parent, so the total must
  // be derived from them rather than from unfiltered summary totals.
  const total = filteredData.length;

  return (
    <div className="kb-status-bar">
      <div className="kb-stat-item kb-stat-item--total">
        <span className="kb-stat-val">{total}</span>
        <span className="kb-stat-label">Total</span>
      </div>
      {stages.map((s) => (
        <React.Fragment key={s.id}>
          <div className="kb-stat-divider" />
          <div
            className={`kb-stat-item kb-stat-item--${s.id.replace(/[^a-z0-9]/gi, "_")}`}
          >
            <span className="kb-stat-val">{counts[s.id] ?? 0}</span>
            <span className="kb-stat-label">{s.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const DateFilterControls = ({
  activeDateMode,
  customStart,
  customEnd,
  onModeChange,
  onCustomStartChange,
  onCustomEndChange,
}) => (
  <div className="date-filter">
    <button
      className={`date-btn ${activeDateMode === "all" ? "active" : ""}`}
      onClick={() => onModeChange("all")}
    >
      All
    </button>
    <button
      className={`date-btn ${activeDateMode === "mtd" ? "active" : ""}`}
      onClick={() => onModeChange("mtd")}
    >
      MTD
    </button>
    <button
      className={`date-btn ${activeDateMode === "ytd" ? "active" : ""}`}
      onClick={() => onModeChange("ytd")}
    >
      YTD
    </button>
    <button
      className={`date-btn date-icon-btn ${activeDateMode === "custom" ? "active" : ""}`}
      onClick={() => onModeChange("custom")}
      title="Custom date range"
      aria-label="Custom date range"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    </button>
    {activeDateMode === "custom" && (
      <div className="custom-date-fields">
        <input
          type="date"
          value={customStart}
          onChange={(e) => onCustomStartChange(e.target.value)}
          aria-label="Start date"
        />
        <input
          type="date"
          value={customEnd}
          onChange={(e) => onCustomEndChange(e.target.value)}
          aria-label="End date"
        />
      </div>
    )}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const EnquiryOverviewPanel = ({ axiosInstance, getApiEndpoint }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [activeDateMode, setActiveDateMode] = useState("all");
  const [customStart, setCustomStart] = useState(
    toDateInputValue(getMonthStart()),
  );
  const [customEnd, setCustomEnd] = useState(toDateInputValue(new Date()));
  const [expandedId, setExpandedId] = useState(null);
  const [viewMode, setViewMode] = useState("kanban"); // "list" | "kanban"
  const [listTab, setListTab] = useState("enquiries"); // "enquiries" | "operations"
  const [operationSummary, setOperationSummary] = useState([]);
  // Real per-operation records (operationId, client, purchaseOrders, ...)
  // for the Kanban board on the Operations tab. `data` above is enquiry-
  // shaped and never carries purchaseOrders, so it can't feed the
  // operations Kanban — this is the actual source for that tab.
  const [operationsData, setOperationsData] = useState([]);
  const operationsFetchIdRef = useRef(0);
  const [selectedEmployee, setSelectedEmployee] = useState("all");

  const SHOW_LIMIT = 50;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = getApiEndpoint("/stage-summary");
      const response = await axiosInstance.get(endpoint);
      const summary = response.data?.data || [];
      setStageSummary(summary);
      // The summary's nested `histories` contain no `createdAt`, so using
      // them directly makes MTD/YTD/custom filters reject every row. Fetch
      // the matching paginated records before putting data into the filterable
      // overview state; their full enquiry payload contains `createdAt`.
      try {
        const detailedEnquiries = await getPagedStageEnquiries(
          summary,
          axiosInstance,
          getApiEndpoint,
        );
        setData(
          detailedEnquiries.length > 0
            ? stageSummaryToEnquiries(detailedEnquiries)
            : stageSummaryToEnquiries(summary),
        );
      } catch (detailError) {
        // Keep the overview usable if the optional enrichment endpoint is
        // unavailable, even though date filtering cannot be applied to the
        // compact summary-only response.
        console.error(
          "[EnquiryOverviewPanel] stage-detail fetch error:",
          detailError,
        );
        setData(stageSummaryToEnquiries(summary));
      }
    } catch (err) {
      console.error("[EnquiryOverviewPanel] stage-summary fetch error:", err);
      setError("Failed to load enquiry stage summary.");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, getApiEndpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchOperationSummary = useCallback(async () => {
    const response = await axiosInstance.get(
      getApiEndpoint("/operations/operation-status-summary"),
    );
    return response.data?.data || [];
  }, [axiosInstance, getApiEndpoint]);

  // /operations/pagination only returns operations for ONE stage at a time
  // (operationStageId is required), so building the flat, card-shaped list
  // the Kanban needs means fetching once per stage and tagging each
  // operation with the stage it was fetched under.
  const OPERATIONS_PAGE_SIZE = 50;

  const fetchOperationsForStages = useCallback(
    async (stages) => {
      const results = await Promise.all(
        stages.map(async (stage) => {
          try {
            const total = Number(
              stage.totalCount ?? stage.totalOperations ?? 0,
            );
            const pageCount = Math.ceil(total / OPERATIONS_PAGE_SIZE);
            if (!stage.operationStageId || pageCount === 0) return [];

            const pages = await Promise.all(
              Array.from({ length: pageCount }, (_, page) =>
                axiosInstance
                  .get(getApiEndpoint("/operations/pagination"), {
                    params: {
                      operationStageId: stage.operationStageId,
                      page,
                      size: OPERATIONS_PAGE_SIZE,
                    },
                  })
                  .then((response) => response.data?.data || []),
              ),
            );

            return pages.flat().map((op) => ({
              ...op,
              operationStageId: stage.operationStageId,
              operationStageName: stage.operationStageName,
            }));
          } catch (err) {
            console.error(
              `[EnquiryOverviewPanel] operations pagination fetch error (stage ${stage.operationStageId}):`,
              err,
            );
            return [];
          }
        }),
      );
      return results.flat();
    },
    [axiosInstance, getApiEndpoint],
  );

  const refreshOperationsTab = useCallback(async () => {
    const fetchId = ++operationsFetchIdRef.current;
    try {
      const summary = await fetchOperationSummary();
      // /operations/operation-status-summary returns a flat list of stages
      // (operationStageId, operationStageName, totalCount) — there is no
      // category wrapper, so use it directly rather than flatMapping a
      // non-existent `.stages` property.
      const stages = summary;
      const ops = await fetchOperationsForStages(stages);
      if (operationsFetchIdRef.current !== fetchId) return; // stale response
      setOperationSummary(summary);
      setOperationsData(ops);
    } catch (err) {
      console.error("[EnquiryOverviewPanel] operations tab fetch error:", err);
    }
  }, [fetchOperationSummary, fetchOperationsForStages]);

  useEffect(() => {
    if (listTab !== "operations") return;
    refreshOperationsTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listTab]);

  // Per-stage totals from the same stage-summary response that supplies the
  // cards. This avoids a second request and, importantly, avoids fetching the
  // deprecated bulk /sales-enquiries endpoint in Overview.
  const [stageSummary, setStageSummary] = useState([]);

  // Paginated per-enquiry stage-history / purchase-order detail, from
  // /pagination/sales-enquiries?enquiryId=...&page=...&size=... . Details are
  // fetched only when a user expands a card, then further pages load as that
  // card's scroll container reaches the bottom.
  //   detailByEnquiry[enquiryId] = { stageHistories, purchaseOrders }
  //   detailPaginationByEnquiry[enquiryId] = { nextPage, hasMore }
  //   loadingDetailIds = Set of enquiryIds currently fetching a page
  const [detailByEnquiry, setDetailByEnquiry] = useState({});
  const [detailPaginationByEnquiry, setDetailPaginationByEnquiry] = useState(
    {},
  );
  const [loadingDetailIds, setLoadingDetailIds] = useState(() => new Set());
  const detailPaginationRef = useRef({});
  const loadingDetailRef = useRef(new Set());

  useEffect(() => {
    detailPaginationRef.current = detailPaginationByEnquiry;
  }, [detailPaginationByEnquiry]);

  const fetchDetailPage = useCallback(
    async (enquiryId, reset = false) => {
      if (enquiryId == null) return;
      if (loadingDetailRef.current.has(enquiryId)) return;

      const existing = detailPaginationRef.current[enquiryId];
      if (!reset && existing && !existing.hasMore) return;

      loadingDetailRef.current.add(enquiryId);
      setLoadingDetailIds(new Set(loadingDetailRef.current));

      const page = reset ? 0 : (existing?.nextPage ?? 1);

      try {
        const response = await axiosInstance.get(
          getApiEndpoint("/pagination/sales-enquiries"),
          { params: { enquiryId, page, size: ENQUIRY_DETAIL_PAGE_SIZE } },
        );
        const records = response.data?.data || [];
        const incomingStageHistories = records.flatMap(
          (r) => r.stageHistories || [],
        );
        const incomingPurchaseOrders = records.flatMap(
          (r) => r.purchaseOrders || [],
        );

        setDetailByEnquiry((prev) => {
          const prevDetail = prev[enquiryId] || {
            stageHistories: [],
            purchaseOrders: [],
          };
          return {
            ...prev,
            [enquiryId]: {
              stageHistories: mergeById(
                reset ? [] : prevDetail.stageHistories,
                incomingStageHistories,
                "enquiryStageHistoryId",
              ),
              purchaseOrders: mergeById(
                reset ? [] : prevDetail.purchaseOrders,
                incomingPurchaseOrders,
                "purchaseOrderId",
              ),
            },
          };
        });

        setDetailPaginationByEnquiry((prev) => ({
          ...prev,
          [enquiryId]: {
            nextPage: page + 1,
            hasMore: records.length === ENQUIRY_DETAIL_PAGE_SIZE,
          },
        }));
      } catch (err) {
        console.error(
          `Failed to fetch paginated details for enquiry ${enquiryId}:`,
          err,
        );
      } finally {
        loadingDetailRef.current.delete(enquiryId);
        setLoadingDetailIds(new Set(loadingDetailRef.current));
      }
    },
    [axiosInstance, getApiEndpoint],
  );

  // Fetch page 0 of the paginated detail lazily — only for the card that's
  // actually expanded, and only the first time it's expanded. This is what
  // replaces the old eager prefetch (which fired one
  // /pagination/sales-enquiries request per enquiry the moment the bulk
  // list loaded — 60+ requests on a page with 60+ rows, regardless of
  // whether the user ever opened a card).
  const handleToggleExpand = useCallback(
    (id) => {
      setExpandedId((current) => {
        const next = current === id ? null : id;
        if (
          next != null &&
          !detailByEnquiry[next] &&
          !loadingDetailRef.current.has(next)
        ) {
          fetchDetailPage(next, true);
        }
        return next;
      });
    },
    [detailByEnquiry, fetchDetailPage],
  );

  const allEmployees = useMemo(() => {
    const set = new Set();
    data.forEach((eq) => {
      (eq.stageHistories || []).forEach((s) => {
        if (s.assignedTo) set.add(s.assignedTo);
      });
      (eq.purchaseOrders || []).forEach((o) =>
        (o.products || []).forEach((p) =>
          (p.operationStages || []).forEach((os) => {
            if (os.assignedEmployeeName) set.add(os.assignedEmployeeName);
          }),
        ),
      );
    });
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const dateWindow = (() => {
      if (activeDateMode === "all") return { start: null, end: null };
      if (activeDateMode === "mtd") {
        return {
          start: toDateInputValue(getMonthStart()),
          end: toDateInputValue(new Date()),
        };
      }
      if (activeDateMode === "ytd") {
        return {
          start: toDateInputValue(getYearStart()),
          end: toDateInputValue(new Date()),
        };
      }
      return { start: customStart, end: customEnd };
    })();

    return data.filter((eq) => {
      if (
        (dateWindow.start || dateWindow.end) &&
        !isWithinRange(getEnquiryDate(eq), dateWindow.start, dateWindow.end)
      ) {
        return false;
      }

      // Text search
      if (q) {
        const haystack = [
          eq.lead?.companyName,
          eq.client?.companyName,
          eq.enquiryCode,
          eq.lead?.contactPerson,
          eq.client?.contactPerson,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      // Employee filter
      if (selectedEmployee !== "all") {
        const matchesEnquiry = (eq.stageHistories || []).some(
          (s) => s.assignedTo === selectedEmployee,
        );
        const matchesOps = (eq.purchaseOrders || []).some((o) =>
          (o.products || []).some((p) =>
            (p.operationStages || []).some(
              (os) => os.assignedEmployeeName === selectedEmployee,
            ),
          ),
        );
        if (!matchesEnquiry && !matchesOps) return false;
      }

      // Quick filter
      if (activeFilter !== "all") {
        const stageNames = (eq.stageHistories || []).map((s) =>
          (s.stageName || "").toLowerCase(),
        );
        // Prefer stageType when the API supplies it; fall back to matching
        // on stageName (same heuristic resolveEnquiryState already uses)
        // so this still works against payloads that don't carry stageType.
        const isWon = eq.stageHistories.some(
          (s) =>
            s.enquiryStatus !== null &&
            (s.stageType === "Won" ||
              (!s.stageType &&
                (s.stageName || "").toLowerCase().includes("won"))),
        );

        const isLost = eq.stageHistories.some(
          (s) =>
            s.enquiryStatus !== null &&
            (s.stageType === "Lost" ||
              (!s.stageType &&
                (s.stageName || "").toLowerCase().includes("lost"))),
        );
        // "Ops Running" / "Completed" depend on eq.purchaseOrders, which is
        // now copied through from the stage-summary `history` record above.
        // If `/stage-summary` doesn't actually include purchaseOrders per
        // history (it's a plausible, lighter-weight endpoint), these two
        // filters will still legitimately show 0 results — that's a data
        // availability question for the backend, not something the client
        // filter logic can work around.
        const hasOpsRunning = (eq.purchaseOrders || []).some((o) =>
          (o.products || []).some((p) =>
            (p.operationStages || []).some((os) => isActiveStatus(os.status)),
          ),
        );
        const currentStage = stageNames[stageNames.length - 1] || "";

        switch (activeFilter) {
          case "new":
            if (currentStage && !currentStage.includes("enquiry")) return false;
            break;
          case "pending":
            // normalizeStatus() defaults a missing status to "PENDING" for
            // badge/display purposes — that default must not leak into
            // filtering, or every enquiry with an unknown status would
            // incorrectly match "Pending".
            if (!eq.status || normalizeStatus(eq.status) !== "PENDING")
              return false;
            break;
          case "design":
            if (!currentStage.includes("design")) return false;
            break;
          case "negotiation":
            if (
              !currentStage.includes("proposal") &&
              !currentStage.includes("negotiation")
            )
              return false;
            break;
          case "won":
            if (!isWon || isLost) return false;
            break;
          case "ops":
            if (!hasOpsRunning) return false;
            break;
          case "completed": {
            const operationStages = (eq.purchaseOrders || []).flatMap((o) =>
              (o.products || []).flatMap((p) => p.operationStages || []),
            );
            const allOpsCompleted =
              isWon &&
              operationStages.length > 0 &&
              operationStages.every((os) => isCompletedStatus(os.status));
            if (!allOpsCompleted) return false;
            break;
          }
          case "lost":
            if (!isLost) return false;
            break;
          case "cancelled":
            if (
              !isCancelledStatus(eq.status) &&
              !(eq.stageHistories || []).some((s) =>
                isCancelledStatus(s.enquiryStatus),
              )
            )
              return false;
            break;
          default:
            // ops_stage::<name> — filter to enquiries whose furthest-reached op stage matches
            if (activeFilter.startsWith("ops_stage::")) {
              const targetStage = activeFilter.slice("ops_stage::".length);
              const furthest = _deriveOpsStageName(eq);
              if (furthest !== targetStage) return false;
            }
            break;
        }
      }

      return true;
    });
  }, [
    data,
    search,
    activeFilter,
    activeDateMode,
    customStart,
    customEnd,
    selectedEmployee,
  ]);

  // Operations tab uses operationsData (real per-operation records fetched
  // via /operations/pagination) rather than `filtered`, which is derived
  // from the enquiry-only /stage-summary data and never carries
  // purchaseOrders. Search applies here too; the ops_stage:: quick filter
  // matches on the stage each operation was fetched under. The operations
  // endpoint now returns `createdAt`, so date filters use it just like the
  // enquiry filters do.
  const opsFiltered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const dateWindow = (() => {
      if (activeDateMode === "all") return { start: null, end: null };
      if (activeDateMode === "mtd") {
        return {
          start: toDateInputValue(getMonthStart()),
          end: toDateInputValue(new Date()),
        };
      }
      if (activeDateMode === "ytd") {
        return {
          start: toDateInputValue(getYearStart()),
          end: toDateInputValue(new Date()),
        };
      }
      return { start: customStart, end: customEnd };
    })();

    return operationsData.filter((op) => {
      if (
        (dateWindow.start || dateWindow.end) &&
        !isWithinRange(
          getOperationDate(op),
          dateWindow.start,
          dateWindow.end,
        )
      ) {
        return false;
      }

      if (q) {
        const haystack = [
          op.client?.companyName,
          op.client?.contactPerson,
          op.operationNo,
          ...(op.purchaseOrders || []).flatMap((order) => [
            order.orderNumber,
            ...(order.products || []).map((product) => product.productName),
          ]),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (activeFilter !== "all" && activeFilter.startsWith("ops_stage::")) {
        const targetStage = activeFilter.slice("ops_stage::".length);
        if (op.operationStageName !== targetStage) return false;
      }
      return true;
    });
  }, [
    operationsData,
    search,
    activeFilter,
    activeDateMode,
    customStart,
    customEnd,
  ]);

  const tabFiltered = useMemo(() => {
    if (listTab === "operations") {
      return opsFiltered;
    }
    return filtered;
  }, [filtered, opsFiltered, listTab]);

  const shown = showAll ? tabFiltered : tabFiltered.slice(0, SHOW_LIMIT);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setShowAll(false);
  };

  const handleFilter = (key) => {
    setActiveFilter(key);
    setShowAll(false);
  };

  const handleDateMode = (mode) => {
    setActiveDateMode(mode);
    setShowAll(false);
  };

  return (
    <div className="esd-wrap">
      {/* ── Sticky header ── */}
      <div className="esd-sticky-header">
        <div className="esd-topbar">
          <div className="esd-title-row">
            <h1 className="esd-title">Enquiry Status</h1>
            {!loading && (
              <span className="esd-count">
                {tabFiltered.length} of {data.length}
              </span>
            )}

            {/* Enquiries / Operations tab — now shown in BOTH List and Kanban views */}
            <div className="esd-view-toggle esd-tab-group">
              {LIST_TABS.map((t) => (
                <button
                  key={t.key}
                  className={`esd-view-btn${listTab === t.key ? " active" : ""}`}
                  onClick={() => {
                    setListTab(t.key);
                    setActiveFilter("all");
                    setShowAll(false);
                    setExpandedId(null);
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* List / Kanban view toggle */}
            <div className="esd-view-toggle">
              <button
                className={`esd-view-btn${viewMode === "kanban" ? " active" : ""}`}
                onClick={() => {
                  setViewMode("kanban");
                }}
              >
                Kanban
              </button>
              <button
                className={`esd-view-btn${viewMode === "list" ? " active" : ""}`}
                onClick={() => {
                  setViewMode("list");
                }}
              >
                List
              </button>
            </div>
          </div>

          <div className="esd-controls">
            <DateFilterControls
              activeDateMode={activeDateMode}
              customStart={customStart}
              customEnd={customEnd}
              onModeChange={handleDateMode}
              onCustomStartChange={(value) => {
                setCustomStart(value);
                setShowAll(false);
              }}
              onCustomEndChange={(value) => {
                setCustomEnd(value);
                setShowAll(false);
              }}
            />

            {/* Employee filter dropdown */}
            {allEmployees.length > 0 && (
              <select
                className="esd-employee-select"
                value={selectedEmployee}
                onChange={(e) => {
                  setSelectedEmployee(e.target.value);
                  setShowAll(false);
                }}
                aria-label="Filter by assigned employee"
              >
                <option value="all">All Employees</option>
                {allEmployees.map((emp) => (
                  <option key={emp} value={emp}>
                    {emp}
                  </option>
                ))}
              </select>
            )}

            {/* Search */}
            <div className="esd-search">
              <svg
                className="search-ico"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search client or enquiry…"
                value={search}
                onChange={handleSearch}
                aria-label="Search enquiries"
              />
              {search && (
                <button
                  className="search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Unified KB status bar — stages from raw data, counts from filtered data */}
        {!loading && (
          <KbStatusBar
            allData={data}
            filteredData={tabFiltered}
            activeTab={listTab}
            operationSummary={operationSummary}
          />
        )}
        {/* Quick filters apply to both list and Kanban views. */}
        <div
          className="esd-filters"
          role="tablist"
          aria-label="Filter enquiries"
        >
            {listTab === "operations"
              ? // The operations response does not include nested
                // operationStages, so build these directly from its summary.
                (() => {
                  const opStages = (operationSummary || [])
                    .slice()
                    .sort(
                      (a, b) =>
                        (a.sequenceNumber ?? a.operationStageId ?? 999) -
                        (b.sequenceNumber ?? b.operationStageId ?? 999),
                    )
                    .filter((stage) => stage.operationStageName)
                    .map((stage) => ({
                      key: `ops_stage::${stage.operationStageName}`,
                      label: stage.operationStageName,
                    }));
                  const allOpsFilter = { key: "all", label: "All" };
                  return [allOpsFilter, ...opStages].map((f) => (
                    <button
                      key={f.key}
                      className={`qf-btn ${activeFilter === f.key ? "active" : ""}`}
                      onClick={() => handleFilter(f.key)}
                      role="tab"
                      aria-selected={activeFilter === f.key}
                    >
                      {f.label}
                    </button>
                  ));
                })()
              : QUICK_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className={`qf-btn ${activeFilter === f.key ? "active" : ""}`}
                    onClick={() => handleFilter(f.key)}
                    role="tab"
                    aria-selected={activeFilter === f.key}
                  >
                    {f.label}
                  </button>
                ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="esd-content">
        {viewMode === "kanban" ? (
          <KanbanBoard
            axiosInstance={axiosInstance}
            getApiEndpoint={getApiEndpoint}
            data={tabFiltered}
            loading={loading}
            error={error}
            onRefresh={() => {
              fetchData();
            }}
            stageSummary={stageSummary}
            activeTab={listTab}
            onTabChange={(tab) => {
              setListTab(tab);
              setShowAll(false);
              setExpandedId(null);
            }}
          />
        ) : loading ? (
          <LoadingScreen />
        ) : error ? (
          <div className="esd-state error">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
            <button onClick={fetchData} className="retry-btn">
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="esd-state">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
            <span>No enquiries match your filters.</span>
            <button
              onClick={() => {
                setSearch("");
                setActiveFilter("all");
                setActiveDateMode("all");
                setSelectedEmployee("all");
              }}
              className="retry-btn"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="esd-main-grid">
            <div className="eq-list" role="list">
              {shown.map((eq) => {
                const id = eq.enquiryId || eq.enquiryCode;
                return (
                  <EnquiryRow
                    key={id}
                    eq={eq}
                    expanded={expandedId === id}
                    onToggle={() => handleToggleExpand(id)}
                    detail={detailByEnquiry[eq.enquiryId]}
                    pagination={detailPaginationByEnquiry[eq.enquiryId]}
                    loadingMore={loadingDetailIds.has(eq.enquiryId)}
                    onLoadMore={fetchDetailPage}
                  />
                );
              })}
              {!showAll && tabFiltered.length > SHOW_LIMIT && (
                <button
                  className="load-more-btn"
                  onClick={() => setShowAll(true)}
                >
                  Show all {tabFiltered.length} enquiries
                </button>
              )}
            </div>
            <SummaryRail data={tabFiltered} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiryOverviewPanel;
